<template>
  <div class="b-qr-vehicle-panel">
    <button
      v-if="panels > 1"
      type="button"
      class="btn btn-link btn-icon-start"
      @click="onRemovePanel">
      <i class="icon-trash"></i>
      {{ dictionary.buttonDeleteText }}
    </button>

    <div class="row gx-5">
      <div class="col-lg-8 offset-lg-1">
        <div class="row gx-5">
          <!-- car brand -->
          <div class="col-lg-6 mb-28">
            <label :for="'brand' + vehicle.id" class="form-label">
              {{ dictionary.carBrandLabel }}
            </label>
            <MultiSelect
              :id="'brand' + vehicle.id"
              v-model="brand"
              autocomplete="off"
              :searchable="true"
              :clear-on-search="true"
              :options="brands"
              :can-deselect="false"
              :no-results-text="dictionary.noResultsText"
              value-prop="value"
              label="text"
              :class="{ 'has-error': v$.brand.$error }"
              :placeholder="dictionary.carBrandPlaceholder" />

            <div v-if="v$.brand.$error">
              <div v-if="v$.brand.required.$invalid" class="error">
                {{ dictionary.carBrandRequired }}
              </div>
            </div>
          </div>
          <!-- model -->
          <div class="col-lg-6 mb-28">
            <label :for="'model' + vehicle.id" class="form-label">
              {{ dictionary.modelLabel }}
            </label>
            <input
              :id="'model' + vehicle.id"
              v-model="model"
              autocomplete="off"
              class="form-control"
              name="model"
              type="text"
              :class="{ 'has-error': v$.model.$error }"
              @blur="v$.model.$touch" />

            <div v-if="v$.model.$error">
              <div v-if="v$.model.required.$invalid" class="error">
                {{ dictionary.modelRequired }}
              </div>
              <!-- prettier-ignore -->
              <div
              v-if="v$.model.minLength.$invalid || v$.model.maxLength.$invalid"
              class="error">
              {{ dictionary.modelLength }}
              </div>
            </div>
          </div>
        </div>
        <div class="row gx-5">
          <!-- fuel type -->
          <div class="col-lg-6 mb-28">
            <label :for="'fuelType' + vehicle.id" class="form-label">
              {{ dictionary.fuelTypeLabel }}
            </label>
            <select
              :id="'fuelType' + vehicle.id"
              v-model="fuelType"
              autocomplete="off"
              name="fuelType"
              class="form-select"
              :class="{ 'has-error': v$.fuelType.$error }">
              <option value="" disabled selected>
                {{ dictionary.fuelTypePlaceholder }}
              </option>
              <option
                v-for="item in fuelTypes"
                :key="item.value"
                :value="item.value">
                {{ item.text }}
              </option>
            </select>

            <div v-if="v$.fuelType.$error">
              <div v-if="v$.fuelType.required.$invalid" class="error">
                {{ dictionary.fuelTypeRequired }}
              </div>
            </div>
          </div>
          <!-- leaseDuration -->
          <div class="col-lg-6 mb-28">
            <label :for="'leaseDuration' + vehicle.id" class="form-label">
              {{ dictionary.yearsLabel }}
            </label>
            <select
              v-if="!durationIsPreselected"
              :id="'leaseDuration' + vehicle.id"
              v-model="leaseDuration"
              autocomplete="off"
              name="leaseDuration"
              class="form-select"
              :class="{ 'has-error': v$.leaseDuration.$error }">
              <option
                v-for="item in leaseDurationOptions"
                :key="item.value"
                :value="item.value">
                {{ item.text }}
              </option>
            </select>
            <input
              v-else
              :id="'leaseDuration' + vehicle.id"
              v-model="leaseDurationText"
              autocomplete="off"
              readonly
              class="form-control"
              name="leaseDuration"
              type="text" />

            <div v-if="v$.leaseDuration.$error">
              <div v-if="v$.leaseDuration.required.$invalid" class="error">
                {{ dictionary.yearsRequired }}
              </div>
            </div>
          </div>
        </div>
        <div class="row gx-5">
          <!-- miles per year -->
          <div class="col-lg-6 mb-28">
            <label :for="'milesPerYear' + vehicle.id" class="form-label">
              {{ dictionary.milesPerYearLabel }}
            </label>
            <select
              :id="'milesPerYear' + vehicle.id"
              v-model="milesPerYear"
              autocomplete="off"
              name="milesPerYear"
              class="form-select"
              :class="{ 'has-error': v$.milesPerYear.$error }">
              <option
                v-for="item in milesPerYearOptions"
                :key="item.value"
                :value="item.value">
                {{ item.text }}
              </option>
            </select>

            <div v-if="v$.milesPerYear.$error">
              <div v-if="v$.milesPerYear.required.$invalid" class="error">
                {{ dictionary.milesPerYearRequired }}
              </div>
            </div>
          </div>
          <!-- number of vehicles -->
          <div class="col-lg-6 mb-28">
            <label :for="'vehicleQuantity' + vehicle.id" class="form-label">
              {{ dictionary.vehicleQuantityLabel }}
            </label>
            <input
              :id="'vehicleQuantity' + vehicle.id"
              v-model.trim="vehicleQuantity"
              autocomplete="off"
              class="form-control"
              name="vehicleQuantity"
              type="number"
              min="1"
              :class="{ 'has-error': v$.vehicleQuantity.$error }"
              @blur="v$.vehicleQuantity.$touch" />

            <div v-if="v$.vehicleQuantity.$error">
              <div v-if="v$.vehicleQuantity.required.$invalid" class="error">
                {{ dictionary.vehicleQuantityRequired }}
              </div>
              <div v-if="v$.vehicleQuantity.integer.$invalid" class="error">
                {{ dictionary.vehicleQuantityNumeric }}
              </div>
              <div v-if="v$.vehicleQuantity.numeric.$invalid" class="error">
                {{ dictionary.vehicleQuantityNumeric }}
              </div>
            </div>
          </div>
        </div>
        <div class="row gx-5">
          <!-- accept right to buy -->
          <div v-if="enableBuyOut" class="col-lg-6 mb-28">
            <input
              :id="'rightToBuy' + vehicle.id"
              v-model="rightToBuy"
              autocomplete="off"
              class="form-check-input"
              type="checkbox" />
            <label
              :for="'rightToBuy' + vehicle.id"
              class="form-check-label semibold">
              {{ dictionary.rightToBuyLabel }}
            </label>
          </div>
          <!-- payment In Advance -->
          <div v-if="enablePaymentInAdvance" class="col-lg-6 mb-28">
            <input
              :id="'paymentInAdvance' + vehicle.id"
              v-model="paymentInAdvance"
              autocomplete="off"
              class="form-check-input"
              type="checkbox" />
            <label
              :for="'paymentInAdvance' + vehicle.id"
              class="form-check-label semibold">
              {{ dictionary.paymentInAdvanceLabel }}
            </label>
          </div>
        </div>
        <!-- additional comments -->
        <div>
          <div class="row">
            <label
              :for="'additionalComments' + vehicle.id"
              class="form-label extrabold">
              {{ dictionary.additionalCommentsLabel }}
            </label>
          </div>

          <div class="row">
            <div class="col mb-15">
              <!-- additionalComments text area -->
              <textarea
                :id="'additionalComments' + vehicle.id"
                v-model="additionalComments"
                class="form-control"
                :class="{ 'has-error': v$.additionalComments.$error }"
                :placeholder="dictionary.additionalCommentsPlaceholder"
                @blur="v$.additionalComments.$touch"></textarea>

              <div v-if="v$.additionalComments.$error">
                <div
                  v-if="v$.additionalComments.maxLength.$invalid"
                  class="error">
                  {{ dictionary.additionalCommentsLength }}
                </div>
              </div>
            </div>
          </div>

          <!-- additionalComments characters -->
          <div class="row">
            <div class="col d-flex justify-content-end">
              <small class="bold">
                <span v-if="commentsLength">{{ commentsLength }} /</span>
                {{ additionalCommentsMaxLength }}
                {{ dictionary.additionalCommentsCharacters }}
                {{ dictionary.charactersLabel }}
              </small>
            </div>
          </div>
          <!--hidden fields-->
          <input
            :id="'vehicleType' + vehicle.id"
            v-model="vehicleType"
            name="vehicleType"
            type="hidden" />
          <input
            :id="'programmeType' + vehicle.id"
            v-model="programmeType"
            name="programmeType"
            type="hidden" />
        </div>
      </div>
      <!-- car brand logo -->
      <div class="col-2 d-none d-lg-block">
        <img v-if="image" :src="image.url" :alt="image.alt" />
        <RoadLottie v-else />
      </div>
    </div>
  </div>
</template>

<script>
import * as lib from '../../../lib/lib'
import useVuelidate from '@vuelidate/core'
import {
  integer,
  maxLength,
  minLength,
  numeric,
  required,
} from '@vuelidate/validators'
import MultiSelect from '@vueform/multiselect'
import RoadLottie from './../lotties/Road.vue'

export default {
  components: {
    MultiSelect,
    RoadLottie,
  },
  props: {
    panels: {
      required: true,
      type: Number,
    },
    vehicle: {
      required: true,
      type: Object,
    },
    enableBuyOut: {
      required: false,
      type: Boolean,
    },
    enablePaymentInAdvance: {
      required: false,
      type: Boolean,
    },
  },
  emits: ['change-comments', 'remove-panel', 'vehicle-data'],

  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      panelId: '',
      brand: '',
      model: '',

      fuelType: '',

      milesPerYear: '',
      vehicleQuantity: 1,

      rightToBuy: false,
      paymentInAdvance: true,

      brands: [],
      fuelTypes: [],
      leaseDurationOptions: [],
      milesPerYearOptions: [],

      leaseDuration: '',
      durationIsPreselected: false,

      additionalComments: '',
      additionalCommentsMaxLength: 400,
      vehicleType: '',
      programmeType: '',
    }
  },
  validations() {
    return {
      brand: {
        required,
      },
      model: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(100),
      },
      fuelType: {
        required,
      },
      leaseDuration: {
        required,
      },
      milesPerYear: {
        required,
      },
      vehicleQuantity: {
        required,
        integer,
        numeric,
      },
      additionalComments: {
        maxLength: maxLength(this.additionalCommentsMaxLength),
      },
    }
  },
  computed: {
    leaseDurationText() {
      if (!this.leaseDurationOptions.length > 0) {
        return ''
      }
      return this.leaseDurationOptions.find(
        (item) => item.value === this.leaseDuration
      ).text
    },
    commentsLength() {
      return this.additionalComments.length
    },
    image() {
      if (!this.brand) {
        return null
      }
      const images = this.brands.find((item) => item.value === this.brand).icon

      return {
        url: images.url,
        alt: images.alt,
      }
    },
  },
  watch: {
    additionalComments() {
      this.onChangeComments()
    },
  },
  mounted() {
    const { brands, fuelTypes, leaseDurationOptions, milesPerYear } =
      lib.getData('quotationData')

    this.brands = brands
    this.fuelTypes = fuelTypes
    this.leaseDurationOptions = leaseDurationOptions
    this.milesPerYearOptions = milesPerYear
    this.panelId = this.vehicle.id

    this.$nextTick(() => {
      this.setPreselectedValues()
      this.setPrefilledValues()
    })
  },
  methods: {
    setPrefilledValues() {
      if (this.vehicle.id) {
        this.panelId = this.vehicle.id
      }
      if (this.vehicle.Brand) {
        this.brand = this.vehicle.Brand
      }
      if (this.vehicle.Model) {
        this.model = this.vehicle.Model
      }
      if (this.vehicle.FuelType) {
        this.fuelType = this.vehicle.FuelType
      }
      if (this.vehicle.LeaseDuration) {
        this.leaseDuration = this.vehicle.LeaseDuration
      }
      if (this.vehicle.MilesPerYear) {
        this.milesPerYear = this.vehicle.MilesPerYear
      }
      if (this.vehicle.vehicleQuantity) {
        this.vehicleQuantity = this.vehicle.VehicleQuantity
      }
      if (this.vehicle.RightToBuy) {
        this.rightToBuy = this.vehicle.RightToBuy
      }
      if (
        this.vehicle.PaymentInAdvance !== null &&
        this.vehicle.PaymentInAdvance !== undefined
      ) {
        this.paymentInAdvance = this.vehicle.PaymentInAdvance
      }
      if (this.vehicle.AdditionalComments) {
        this.additionalComments = this.vehicle.AdditionalComments
      }
      if (this.vehicle.DurationIsPreselected) {
        this.durationIsPreselected = this.vehicle.DurationIsPreselected
      }
      if (this.vehicle.vehicleType) {
        this.vehicleType = this.vehicle.vehicleType
      }
      if (this.vehicle.programmeType) {
        this.programmeType = this.vehicle.programmeType
      }
    },
    collectData() {
      const vehicleData = {
        id: this.panelId,
        Brand: this.brand,
        Model: this.model,
        FuelType: this.fuelType,
        LeaseDuration: this.leaseDuration,
        MilesPerYear: this.milesPerYear,
        VehicleQuantity: this.vehicleQuantity,
        RightToBuy: this.rightToBuy,
        PaymentInAdvance: this.paymentInAdvance,
        AdditionalComments: this.additionalComments,
        VehicleType: this.vehicleType,
        ProgrammeType: this.programmeType,
      }
      this.$emit('vehicle-data', vehicleData)
    },
    setPreselectedValues() {
      if (!this.vehicle.LeaseDuration) {
        const entry = this.leaseDurationOptions.find((i) => i.selected)
        this.leaseDuration = entry ? entry.value : ''
      }

      const entry = this.milesPerYearOptions.find((i) => i.selected)
      this.milesPerYear = entry ? entry.value : ''
    },

    onChangeComments() {
      this.$emit('change-comments', this.additionalComments)
    },

    onRemovePanel() {
      this.$emit('remove-panel', this.panelId)
    },
  },
}
</script>
